/**
 * <JsonLd> — injects a Schema.org `<script type="application/ld+json">` into
 * <head> and removes it on unmount (Story 30.4). Same cleanup discipline as
 * useSEO: no leak across SPA navigation.
 *
 * Unlike useSEO's shared singleton tags, each <JsonLd> owns its OWN script
 * element (per-instance, no ref-counting) — a page can render several
 * (Organization + SoftwareApplication + FAQPage) and each cleans up its own.
 *
 * Security: the schema object is serialised with JSON.stringify and written to
 * `script.textContent` (never innerHTML), and `<`/`>`/`&` are escaped so a
 * value containing "</script>" cannot break out of the script element.
 */
import { useEffect } from "react";
import type { JsonLdObject } from "./schemas";

/** Escape characters that could terminate the <script> element or start an entity. */
function safeSerialize(data: JsonLdObject): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

export function JsonLd({ data }: { data: JsonLdObject }): null {
  // Depend ONLY on the serialized string + a string type key. Callers pass a
  // fresh object literal each render (e.g. `organizationSchema()` inline in
  // JSX); depending on the object identity would re-inject the script on every
  // render (churn). `json`/`typeKey` are stable by VALUE, so the effect re-runs
  // only when the schema content actually changes.
  const json = safeSerialize(data);
  const typeKey = String(data["@type"] ?? "schema");
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld", typeKey);
    script.textContent = json;
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [json, typeKey]);
  return null;
}
